import { kindEyebrow } from '../copyKinds'
import type { Item } from './api'
import { r4 } from '../copy'

const TZ = 'America/Los_Angeles'

const whenFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
})
const dayFmt = new Intl.DateTimeFormat('en-US', { timeZone: TZ, month: 'short', day: 'numeric' })

/** "Sat Oct 4 · 7:30 PM", in Los Angeles time. A start at exactly midnight is a date without a time (a date_only row, e.g. a
 *  parade whose page gives the day only): "Sat Nov 29", never "12:00 AM" (B-203). */
export function formatWhen(iso: string): string {
  const p: Record<string, string> = {}
  for (const part of whenFmt.formatToParts(new Date(iso))) p[part.type] = part.value
  const day = `${p.weekday} ${p.month} ${p.day}`
  return p.hour === '12' && p.minute === '00' && p.dayPeriod === 'AM' ? day : `${day} · ${p.hour}:${p.minute} ${p.dayPeriod}`
}

/** "7:30 PM" in a given zone. A date_only row starts at midnight, which callers show as the day only (B-204). */
export const timeIn = (iso: string, tz: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz })
/** True for a formatted time of midnight; newer browsers put a narrow no-break space before AM, so spaces are normalized. */
export const isMidnightText = (t: string) => t.replace(/\s/g, ' ').endsWith('12:00 AM')

const rangeFmt = new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric' })

/** "Thu Oct 1 – Wed Oct 7": the seven days from this instant, in Los Angeles time. */
export function formatWeekRange(iso: string): string {
  const start = new Date(iso)
  const end = new Date(start.getTime() + 6 * 86400e3)
  const f = (d: Date) => rangeFmt.format(d).replace(',', '')
  return `${f(start)} – ${f(end)}`
}

/** Dated items first, soonest first; undated activities last (stable otherwise). */
export function byDate<T extends { start_at: string | null }>(a: T, b: T): number {
  if (a.start_at && b.start_at) return new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
  if (a.start_at) return -1
  if (b.start_at) return 1
  return 0
}

/** "Oct 1", in Los Angeles time. */
export function formatDay(iso: string): string {
  return dayFmt.format(new Date(iso))
}

/** "Venue · Neighborhood", without repeating a neighborhood the venue name already contains. */
export function placeLine(item: Item, name: string = item.title): string {
  // R9: the line under a name never repeats it (new-city and map-sourced rows have venue = title): the neighborhood
  // or the address then, else nothing
  const same = (x: string) => sameName(x, name)
  const rawVenue = item.venue_name?.trim() ?? ''
  const venue = same(rawVenue) ? '' : rawVenue
  const hood = item.neighborhood?.trim() && !same(item.neighborhood) ? item.neighborhood.trim() : ''
  if (!venue && rawVenue && !hood) {
    const addr = item.address?.trim() ?? ''
    return addr && !same(addr) ? addr : ''
  }
  if (venue && hood && venue.toLowerCase().includes(hood.toLowerCase())) return venue
  return [venue, hood].filter(Boolean).join(' · ')
}

/** Two names for the same place, ignoring case, spacing and punctuation ("Embarcadero Marina Park" twice). */
export function sameName(a: string | null | undefined, b: string | null | undefined): boolean {
  const k = (x: string | null | undefined) => (x ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')
  const ka = k(a)
  return ka !== '' && ka === k(b)
}

/** Category and tag slugs are data, shown as plain words: "day-trip" -> "Day trip". */
/** Level-2 kinds (10/18) ride on tags as `sub:<slug>` (pipeline/subs.py). */
export const isKindTag = (t: string) => t.startsWith('sub:')
/** The listing's first kind as its eyebrow words ("sub:electronic" -> "Electronic", Astra's names in copyKinds.ts), or null. */
export function kindText(tags?: readonly string[] | null): string | null {
  const t = (tags ?? []).find(isKindTag)
  return t ? kindEyebrow(t.slice(4)) : null
}

export function slugLabel(slug: string, capitalize = false): string {
  const s = slug.replace(/-/g, ' ')
  return capitalize ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

/** "Under the radar" needs evidence, not ignorance (10/09): a place read off the open map (OpenStreetMap / Overture) with no
 *  page of its own never carries the badge, whatever its score. Same rule as the server (supabase/functions/_shared/verify.ts). */
export function isUnderTheRadar(item: Pick<Item, 'iykyk_score' | 'url'> & { judged_by?: string | null }): boolean {
  if ((item.iykyk_score ?? 0) < 0.7) return false
  const mapOnly = ['osm-city', 'osm-area', 'overture'].includes(String(item.judged_by ?? ''))
  const ownPage = /^https?:\/\//i.test(item.url ?? '') && !/^https?:\/\/([a-z0-9-]+\.)*(openstreetmap\.org|overturemaps\.org)(\/|$|\?|#)/i.test(item.url ?? '')
  return !mapOnly || ownPage
}

export function fileSlug(title: string): string {
  return title.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'event'
}

/** Server messages arrive lowercase ("that invite link does not work anymore"); show them as a sentence. */
export function sentence(message: string): string {
  const m = message.trim()
  if (!m) return m
  const s = m.charAt(0).toUpperCase() + m.slice(1)
  return /[.!?]$/.test(s) ? s : `${s}.`
}

export function errorMessage(e: unknown): string {
  if (e instanceof Error && e.message) return sentence(e.message)
  if (typeof e === 'object' && e && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return sentence((e as { message: string }).message)
  }
  return ''
}

export function wordCount(s: string): number {
  return s.trim().split(/\s+/).filter(Boolean).length
}

/**
 * A source's price as people say it: "free w/rsvp / free" -> "Free with RSVP", "$40.00" -> "$40", "FREE" -> "Free".
 * Only the presentation changes; every condition and number in the source stays.
 */
export function normalizePrice(raw: string): string {
  let s = raw.trim().replace(/\s+/g, ' ')
  if (!s) return s
  const letters = s.replace(/[^A-Za-z]/g, '')
  if (letters.length > 2 && letters === letters.toUpperCase()) s = s.toLowerCase()
  s = s
    .replace(/\bw\/\s*/gi, 'with ')
    .replace(/\bb4\b/gi, 'before')
    .replace(/\brsvp\b/gi, 'RSVP')
    .replace(/\$(\d+)\.00\b/g, '$$$1')
  // "free with RSVP / free": a part that only repeats the start of another part says nothing new
  const parts = s.split(/\s+\/\s+/)
  const kept = parts.filter((p, i) => {
    const low = p.toLowerCase()
    return !parts.some((o, j) => j !== i && (o.toLowerCase() === low ? j < i : o.toLowerCase().startsWith(low + ' ')))
  })
  const out = kept.join(' / ')
  return out.charAt(0).toUpperCase() + out.slice(1)
}

const TIER: Record<string, string> = { cheap: '$', mid: '$$', splurge: '$$$' }

/** The price line on every card: the source's words, else Free, else $/$$/$$$ from the cost tier, else "not listed". */
export function priceLine(item: Pick<Item, 'price_text' | 'is_free' | 'cost_tier'>): string {
  if (item.price_text?.trim()) return normalizePrice(item.price_text)
  if (item.is_free || item.cost_tier === 'free') return r4('price.free')
  if (item.cost_tier && TIER[item.cost_tier]) return TIER[item.cost_tier]
  return r4('price.unknown')
}

/**
 * An RSVP event placed by its area only (tech week and similar: the host shares the street address after the RSVP).
 * The point is the area's center, so no map pin. Places without an address (a trail, a beach) are not this case.
 */
export const areaOnly = (it: { kind: string; rsvp?: string | null; address: string | null; neighborhood: string | null }) =>
  it.kind === 'event' && Boolean(it.rsvp) && !(it.address && /\d/.test(it.address)) && Boolean(it.neighborhood)
